/**
 * One-call facade for building the per-turn page context that gets
 * shipped to Claude: tag every interactive element with a stable ref,
 * serialize the DOM into a compact text summary, and rasterize the
 * viewport in parallel so we don't block on the screenshot.
 */

import type { PageContext } from "../types";
import type { TaggedElement } from "./InteractiveElementTagger";
import { tagInteractiveElements, clearInteractiveElementTags } from "./InteractiveElementTagger";
import { serializePageContext } from "./DOMSerializer";

export interface PageContextBuildResult {
  context: PageContext;
  taggedElements: readonly TaggedElement[];
}

export async function buildPageContext(): Promise<PageContextBuildResult> {
  const taggedElements = tagInteractiveElements();
  const { summary } = serializePageContext(taggedElements);

  // Lazy-load the screenshotter on first use.
  const { captureViewportScreenshot } = await import("./Screenshotter");
  const screenshot = await captureViewportScreenshot();

  const context: PageContext = {
    domSummary: summary,
    screenshotBase64: screenshot.base64,
    screenshotMediaType: screenshot.mediaType,
    viewportWidth: screenshot.widthPixels,
    viewportHeight: screenshot.heightPixels,
  };

  return { context, taggedElements };
}

export function releasePageContext(): void {
  clearInteractiveElementTags();
}
